import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { TextAreaComponents, TextAreaProps } from './types'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import createComponent from '../../../styles-system/create-component'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { getSpace, omitSpace } from '../../../utils/props-utils'
import sizeSynchronize from '../../../utils/size-synchronize'
import { formikField } from '../../_adaptors'
import { BoxPrivate } from '../box/box'
import { Spinner } from '../spinner'

const Component = createComponent('textarea', 'csg-textarea')

export const TextAreaBase = React.forwardRef<
  HTMLElement,
  TextAreaProps & PrivateThemeUiProps
>(function TextAreaBase(props, ref) {
  const { variant = 'textarea', __css, ...rest } = props
  return (
    <Component
      ref={ref}
      variant={variant}
      {...rest}
      __themeKey="forms"
      __css={{
        ...__css
      }}
    />
  )
})

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const TextArea: FC<TextAreaProps> & TextAreaComponents = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaProps & { theme?: Theme }
>(function TextArea(props, ref) {
  const theme = useSafeThemeUI(props)
  const {
    variant,
    a11yLabel,
    a11yId,
    isLoading,
    size,
    state,
    isRequired,
    disabled,
    readOnly,
    resize = 'vertical',
    height,
    onChange,
    className,
    ...rest
  } = props
  const { fontSize, spinnerSize } = useMemo(
    () => sizeSynchronize(size, theme, -1, -1),
    [size, theme]
  )

  return (
    <BoxPrivate
      fontSize={fontSize}
      __css={{
        position: 'relative',
        width: '100%'
      }}
      {...getSpace(rest)}
    >
      <TextAreaBase
        ref={ref}
        id={a11yId}
        className={classNames('csg-tui-textarea', className)}
        variant={variant ? `textarea.${variant}` : 'textarea.default'}
        aria-invalid={state === 'danger'}
        aria-label={a11yLabel}
        aria-required={isRequired}
        disabled={disabled}
        readOnly={readOnly}
        state={state}
        resize={resize}
        height={height}
        onChange={onChange}
        __css={{
          boxSizing: 'border-box',
          width: '100%',
          emPaddingY: 'xs-2',
          emPaddingX: 'xs',
          borderRadius: 'default'
        }}
        {...omitSpace(rest)}
      />
      {isLoading && (
        <Spinner
          variant={`spinners.buttons.${variant}`}
          size={spinnerSize}
          position={'absolute'}
          top={'xxs'}
          right={'sm'}
          zIndex={2}
        />
      )}
    </BoxPrivate>
  )
})

TextArea.Formik = formikField(TextArea, {
  hasFieldWrapper: true
})

TextArea.defaultProps = {
  size: 'sm'
}
export default TextArea
